import React, { useState, useRef, useEffect } from 'react';
import { Trash2, SquarePen, ExternalLink, Sparkles } from 'lucide-react';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { showToast } from '../../../utils/toastManager';
import '../../../styles/resume-editor.css';

const SHOW_PROJECTS_AI_TOOLS = false;

interface Project {
  uid: string;
  title: string;
  headline: string;
  link?: string;
  linkAlias?: string;
  description?: string;
  releaseDate?: string | null;
  yearOnly?: boolean;
  hide?: boolean;
  achievements?: string[];
}

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (uid: string) => void;
  onReorderAchievements?: (uid: string, newOrder: string[]) => void;
  cvUid?: string;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit, onDelete, onReorderAchievements, cvUid }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hoveredAchievementIndex, setHoveredAchievementIndex] = useState<number | null>(null);
  const [hoveredActionIndex, setHoveredActionIndex] = useState<{ achievementIndex: number; actionIndex: number } | null>(null);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (project.yearOnly) {
      return date.getFullYear().toString();
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const releaseDateFormatted = project.releaseDate ? formatDate(project.releaseDate) : '';

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    const achievements = project.achievements || [];
    if (draggedIndex === null || draggedIndex === dropIndex || achievements.length === 0) {
      setDraggedIndex(null);
      return;
    }

    const newAchievements = [...achievements];
    const [removed] = newAchievements.splice(draggedIndex, 1);
    newAchievements.splice(dropIndex, 0, removed);

    if (onReorderAchievements) {
      onReorderAchievements(project.uid, newAchievements);
    }

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editingAchievementIndex !== null && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [editingAchievementIndex]);

  const handleEditAchievement = (index: number) => {
    const achievements = project.achievements || [];
    if (achievements[index] !== undefined) {
      setEditingText(achievements[index]);
      setEditingAchievementIndex(index);
      setHoveredAchievementIndex(null);
    }
  };

  const handleSaveAchievement = (index: number) => {
    if (onReorderAchievements) {
      const achievements = project.achievements || [];
      const trimmedText = editingText.trim();
      if (trimmedText === '') {
        const newAchievements = achievements.filter((_, i) => i !== index);
        onReorderAchievements(project.uid, newAchievements);
      } else {
        const newAchievements = [...achievements];
        newAchievements[index] = trimmedText;
        onReorderAchievements(project.uid, newAchievements);
      }
    }
    setEditingAchievementIndex(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    if (editingAchievementIndex !== null && onReorderAchievements) {
      const achievements = project.achievements || [];
      const achievement = achievements[editingAchievementIndex];
      if (achievement === '') {
        const newAchievements = achievements.filter((_, i) => i !== editingAchievementIndex);
        onReorderAchievements(project.uid, newAchievements);
      }
    }
    setEditingAchievementIndex(null);
    setEditingText('');
  };

  const handleAddAchievement = () => {
    if (onReorderAchievements) {
      const currentAchievements = project.achievements || [];
      const newAchievements = [...currentAchievements, ''];
      onReorderAchievements(project.uid, newAchievements);
      const newIndex = newAchievements.length - 1;
      setEditingText('');
      setEditingAchievementIndex(newIndex);
    }
  };

  return (
    <div className="resume-editor__experience-card">
      <div className="resume-editor__experience-card-header">
        <div className="resume-editor__experience-card-info">
          <h3 className="resume-editor__experience-card-title">{project.title}</h3>
          <div className="resume-editor__experience-card-company-row">
            <div className="resume-editor__experience-card-company">
              <p className="resume-editor__experience-card-subtitle">{project.headline}</p>
            </div>
          </div>
        </div>
        <div className="resume-editor__experience-card-menu-container" ref={menuRef}>
          <button 
            className="resume-editor__experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {releaseDateFormatted && !project.hide && (
            <p className="resume-editor__experience-card-date">{releaseDateFormatted}</p>
          )}
          {menuOpen && (
            <div className="resume-editor__experience-card-dropdown">
              <button
                className="resume-editor__experience-card-dropdown-item"
                onClick={() => {
                  onEdit(project);
                  setMenuOpen(false);
                }}
              >
                Edit project
              </button>
              <button
                className="resume-editor__experience-card-dropdown-item resume-editor__experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(project.uid);
                  setMenuOpen(false);
                }}
              >
                Delete project
              </button>
            </div>
          )}
        </div>
      </div>

      {project.description && (
        <p className="resume-editor__experience-card-company-description">{project.description}</p>
      )}

      {project.link && (
        <div className="resume-editor__experience-card-link">
          <ExternalLink size={14} style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }} />
          <a href={project.link} target="_blank" rel="noopener noreferrer">{project.linkAlias || project.link}</a>
        </div>
      )}

      <div className="resume-editor__experience-card-body">
        <div className="resume-editor__experience-card-achievements">
          {(project.achievements || []).map((achievement, index) => (
              editingAchievementIndex === index ? (
                <div
                  key={index}
                  className="resume-editor__experience-card-achievement-tile resume-editor__experience-card-achievement-tile--editing"
                >
                  <textarea
                    ref={textareaRef}
                    className="resume-editor__experience-card-achievement-textarea"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveAchievement(index);
                      }
                      if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    rows={2}
                  />
                  <div className="resume-editor__experience-card-achievement-edit-actions">
                    <button
                      className="resume-editor__experience-card-achievement-save-btn"
                      onClick={() => handleSaveAchievement(index)}
                    >
                      Save
                    </button>
                    <button
                      className="resume-editor__experience-card-achievement-cancel-btn"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  key={index}
                  className={`resume-editor__experience-card-achievement-tile ${
                    draggedIndex === index ? 'resume-editor__experience-card-achievement-tile--dragging' : ''
                  } ${
                    dragOverIndex === index ? 'resume-editor__experience-card-achievement-tile--drag-over' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => setHoveredAchievementIndex(index)}
                  onMouseLeave={() => setHoveredAchievementIndex(null)}
                  onClick={() => handleEditAchievement(index)}
                >
                  <span className="resume-editor__experience-card-achievement-text">{achievement}</span>
                  {hoveredAchievementIndex === index && (
                    <div className="resume-editor__experience-card-achievement-actions">
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 0 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAchievement(index);
                          }}
                        >
                          <SquarePen size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 0 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Edit</span>
                        )}
                      </div>
                      {SHOW_PROJECTS_AI_TOOLS && (
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 1 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (cvUid) {
                              try {
                                await api.post(API_ENDPOINTS.aiCv.createTask, {
                                  cv_uid: cvUid,
                                  entity_type: 'projects_achievement',
                                  entity_uid: project.uid,
                                  improvement_type: 'current',
                                  old_value: { value: [achievement] },
                                });
                                if ((window as any).__refreshAITasks) {
                                  (window as any).__refreshAITasks();
                                }
                              } catch (error: any) {
                                console.error('Failed to create AI CV task for achievement:', error);
                                const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                                showToast(errorMessage, 'error');
                              }
                            }
                          }}
                        >
                          <Sparkles size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 1 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Rewrite with AI</span>
                        )}
                      </div>
                      )}
                      <div
                        className="resume-editor__experience-card-achievement-action-wrapper"
                        onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 2 })}
                        onMouseLeave={() => setHoveredActionIndex(null)}
                      >
                        <button
                          className="resume-editor__experience-card-achievement-action resume-editor__experience-card-achievement-action--delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (project.achievements && onReorderAchievements) {
                              const newAchievements = project.achievements.filter((_, i) => i !== index);
                              onReorderAchievements(project.uid, newAchievements);
                            }
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                        {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 2 && (
                          <span className="resume-editor__experience-card-achievement-tooltip">Remove</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            ))}
        </div>
        <div className="resume-editor__experience-card-actions">
          <button 
            className="resume-editor__add-achievement-btn"
            onClick={handleAddAchievement}
          >
            + Achievement
          </button>
          {SHOW_PROJECTS_AI_TOOLS && (
          <button 
            className="resume-editor__ai-suggestions-btn"
            onClick={async (e) => {
              e.stopPropagation();
              if (cvUid) {
                try {
                  await api.post(API_ENDPOINTS.aiCv.createTask, {
                    cv_uid: cvUid,
                    entity_type: 'projects_block',
                    entity_uid: project.uid,
                    improvement_type: 'new',
                  });
                  if ((window as any).__refreshAITasks) {
                    (window as any).__refreshAITasks();
                  }
                } catch (error: any) {
                  console.error('Failed to create AI CV task for project:', error);
                  const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                  showToast(errorMessage, 'error');
                }
              }
            }}
          >
            <Sparkles size={14} />
            <span>AI Suggestions</span>
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

